/* Edit model for the loss grid the quote and application pages share — no network, no React.
   During an edit session AG Grid owns the row objects: it writes cell values straight into them,
   and removal/revert/restore flags live on them too, so there is exactly one place to read state from and
   AG Grid's own undo stack has the stable row identity it needs to redo/undo against. Only the
   clones `cloneRowsForEdit` hands out are ever mutated in place (by AG Grid and the toggle
   functions below) — the server's own row objects are never touched. `snapshotRows` freezes what
   "changed" is measured against, which is also what makes Discard free. */

// Skyward rows are limited to the correctable set in reporting's SkywardClaimCorrectionService;
// sending anything else is a 400. Carrier and claim number stay out because they identify the feed
// line itself. Self-reported rows have no such overlay, so every grid column they own is editable.
const SKYWARD_EDITABLE = ['lossCause', 'description', 'incurred', 'expense', 'recovery', 'locationId', 'occurredAt'];
const SELF_EDITABLE = ['carrier', 'claimNumber', 'occurredAt', 'status', 'locationId', 'lossCause', 'expense', 'recovery', 'incurred', 'description'];

export const isSkyward = (row) => !!row && row.source === 'SKYWARD';

const editableFieldsFor = (row) => (isSkyward(row) ? SKYWARD_EDITABLE : SELF_EDITABLE);

export const lossRowKey = (row) => {
  if (row._newKey) {
    return `new:${row._newKey}`;
  }
  return isSkyward(row) ? `sky:${row.claimLineKey}` : `self:${row.id}`;
};

export const rowLabel = (row) => row.claimNumber || (row._newKey || row._new ? 'New loss' : 'Loss');

/* 'other' marks a cause whose text the row carries in lossCauseOther, and the grid's editor for this
   column is a select over the product's cause vocabulary, which has no option that represents it.
   Committing that editor on such a row would write whichever cause the select landed on, and
   toDetailPayload would then null the text beside it — so the cell displays the free text (see the
   column's valueFormatter) and refuses to be edited. There is no editor for the text itself. */
export const isFieldEditable = (row, field) => {
  if (field === 'lossCause' && row && row.lossCause === 'other') {
    return false;
  }
  return editableFieldsFor(row).includes(field);
};

export const newLossRow = (key) => ({ _newKey: key, source: 'SELF_REPORTED', status: 'open' });

const sameValue = (a, b) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a === 'number' || typeof b === 'number') return Number(a) === Number(b);
  return String(a) === String(b);
};

/** Locked-mode display rows: keyed but never edited, so a per-render shallow copy is enough. */
export const withRowKeys = (rows) => rows.map((row) => ({ ...row, _key: lossRowKey(row), _new: !!row._newKey }));

/** Per-row clones for an edit session — these, and only these, are safe for AG Grid to mutate. */
export const cloneRowsForEdit = (rows) => withRowKeys(rows).map((row) => ({ ...row, _removed: false, _reverted: false, _restored: false }));

/** A pristine per-key copy of the rows at the moment edit mode opened, for diffing and Discard. */
export const snapshotRows = (rows) => rows.reduce((out, row) => ({ ...out, [lossRowKey(row)]: { ...row } }), {});

/* Compares each row's currently-editable fields against its snapshot entry. A row with no
   snapshot entry (added this session) diffs against {}, so every non-blank editable field it
   carries counts as an edit — that is what carries a new row's values into the create call. */
export const diffRows = (rows, snapshot) =>
  rows.reduce((out, row) => {
    const original = snapshot[row._key] || {};
    const fields = editableFieldsFor(row).reduce((acc, field) => {
      if (!sameValue(row[field], original[field])) {
        acc[field] = row[field];
      }
      return acc;
    }, {});
    if (Object.keys(fields).length) {
      out[row._key] = fields;
    }
    return out;
  }, {});

/** Display rows carrying `_edited`, the shape validation and save consume. */
export const decorateRows = (rows, edits) => rows.map((row) => ({ ...row, _edited: Object.keys(edits[row._key] || {}) }));

export const appendNewRow = (rows, key) => {
  const row = newLossRow(key);
  return [...rows, { ...row, _key: lossRowKey(row), _new: true, _removed: false, _reverted: false }];
};

/* Parsed CSV rows become ordinary new rows, so everything downstream — validation, Discard, the
   per-row create on Save, restaging after a partial failure — treats them exactly like a row added
   by hand. Values land after newLossRow's defaults so an imported status wins over 'open'. */
export const appendImportedRows = (rows, imported, makeKey) => [
  ...rows,
  ...imported.map((values) => {
    const row = { ...newLossRow(makeKey()), ...values };
    return { ...row, _key: lossRowKey(row), _new: true, _removed: false, _reverted: false, _restored: false };
  }),
];

// A row cannot be both removed and reverted to the feed; whichever the user just toggled on wins.
// Restoring is exclusive with both: it undoes the exclusion the removal would create.
export const toggleRowRemoval = (row) => {
  row._removed = !row._removed;
  if (row._removed) {
    row._reverted = false;
    row._restored = false;
  }
  return row;
};

export const toggleRowRevert = (row) => {
  row._reverted = !row._reverted;
  if (row._reverted) {
    row._removed = false;
    row._restored = false;
  }
  return row;
};

// Only offered on an already-excluded row, so there is nothing for it to be mutually exclusive
// with except a removal staged in the same session — see toggleRowRemoval.
export const toggleRowRestore = (row) => {
  row._restored = !row._restored;
  if (row._restored) {
    row._removed = false;
  }
  return row;
};

export const isDirty = (rows, snapshot) =>
  rows.some((row) => row._new || row._removed || row._reverted || row._restored) || Object.keys(diffRows(rows, snapshot)).length > 0;

const pick = (obj, keys) =>
  keys.reduce((out, key) => {
    if (obj[key] !== undefined) out[key] = obj[key];
    return out;
  }, {});

/** After a partial save, re-applies only the failed rows' edited fields and flags onto fresh clones. */
export const restageFailures = (rows, failedDisplayRows) =>
  failedDisplayRows.reduce((acc, failed) => {
    const editedValues = pick(failed, failed._edited || []);

    if (failed._new) {
      const key = failed._key.slice('new:'.length);
      return [...acc, { ...newLossRow(key), _key: failed._key, _new: true, _removed: false, _reverted: false, ...editedValues }];
    }

    const index = acc.findIndex((row) => row._key === failed._key);
    if (index === -1) {
      // The server no longer returns this row — nothing to restage it onto.
      return acc;
    }
    const updated = { ...acc[index], ...editedValues, _removed: !!failed._removed, _reverted: !!failed._reverted, _restored: !!failed._restored };
    return [...acc.slice(0, index), updated, ...acc.slice(index + 1)];
  }, rows);
